import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { CssVarsProvider } from '@mui/joy/styles';

import RegistrationForm from './components/RegistrationForm';
import Payment from './components/Payment';
import EventPass from './components/EventPass';
import Certificate from './components/Certificate';
import EventList from './components/WorkshopList';
import Login from './components/admin/Login';
import UserHome from './components/admin/UserHome';
import EventManagement from './components/admin/EventManagement';
import CouponManagement from './components/admin/CouponManagement';
import Status from './components/Status';
import Footer from './components/Footer';
import OtpVerification from './components/OtpVerification';
import EventGroupManagement from './components/admin/EventGroupManagement';
import GroupedEvents from './components/GroupedEvents';
import QuickEnqueryForm from './components/QuickEnqueryForm';
import PaymentLinkDashboard from './components/paymentlink/PaymentLinkDashboard';
import PaymentLink from './components/paymentlink/PaymentLink';
import PaymentLinkv1 from './components/paymentlink/PaymentLinkv1';
import ThankYou from './components/ThankYou';
import ShortUrlManagement from './components/admin/ShortUrlManagement';
import Reports from './components/admin/Reports';
import CertificateManagement from './components/admin/CertificateManagement';
import AttendanceManagement from './components/admin/AttendanceManagement';
import CertificateAnalytics from './components/admin/CertificateAnalytics';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const AppContent = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isRegistrationPage = location.pathname.includes('/register/');
  const isPaymentStatusPage = location.pathname.includes('/status/');
  const isPaymentPage = location.pathname.includes('/payment/');
  const isQuickEnqueryPage = location.pathname.includes('/lead/');
  const isEventPassPage = location.pathname.includes('/event-pass/');
  const isGroupedEventPage = location.pathname.includes('/groupedevent/');
  const isOtpVerificationPage = location.pathname.includes('/verify-otp/');
  const isPaymentLinkPage = location.pathname.includes('/pay/');
  const isPaymentLinkPagev1 = location.pathname.includes('/payv1/')
  const isAdminPage = location.pathname.includes('/admin');
  const isThankYouPage = location.pathname.includes('/thankyou/');
  const isPhonepeDashboardPage = location.pathname.includes('/dashboard');
  
  const [anchorEl, setAnchorEl] = useState(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  
  // Admin menu items
  const adminMenuItems = [
    { label: 'Events', path: '/admin/events' },
    { label: 'Coupons', path: '/admin/coupons' },
    { label: 'Event Groups', path: '/admin/event-groups' },
    { label: 'Short URLs', path: '/admin/shorturls' },
    { label: 'Reports', path: '/admin/reports' },
    { label: 'Attendance', path: '/admin/attendance' },
    { label: 'Certificates', path: '/admin/certificates' },
    { label: 'Certificate Analytics', path: '/admin/certificate-analytics' },
  ];

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };
  
  console.log("location.pathname: ", location.pathname);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh'
    }}>
      {!isHomePage && !isRegistrationPage && !isPaymentStatusPage && !isPaymentPage && !isEventPassPage && !isOtpVerificationPage && !isGroupedEventPage && !isQuickEnqueryPage && !isPaymentLinkPage && !isPhonepeDashboardPage && !isPaymentLinkPagev1 && !isThankYouPage && (
        <AppBar position="static">
          <Toolbar id="app-toolbar" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Typography 
              variant="h6" 
              component="div" 
              className="app-title"
              sx={{ flexGrow: { xs: 1, sm: 0 }, minWidth: { xs: 'auto', sm: 200 } }}
            >
              Vector India Events
            </Typography>
            {!isAdminPage ? (
              <Button color="inherit" component={Link} to="/admin">
                Admin
              </Button>
            ) : (
              <>
                {/* Desktop: Show buttons that fit, rest in "More" menu */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: 1, flexGrow: 1 }}>
                  {adminMenuItems.slice(0, 5).map((item) => (
                    <Button
                      key={item.path}
                      color="inherit"
                      component={Link}
                      to={item.path}
                      sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
                    >
                      {item.label}
                    </Button>
                  ))}
                  {adminMenuItems.length > 5 && (
                    <>
                      <Button
                        color="inherit"
                        onClick={handleMenuOpen}
                        sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
                      >
                        More
                      </Button>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >
                        {adminMenuItems.slice(5).map((item) => (
                          <MenuItem
                            key={item.path}
                            component={Link}
                            to={item.path}
                            onClick={handleMenuClose}
                          >
                            {item.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </Box>

                {/* Tablet/Mobile: Show hamburger menu with all items */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1, justifyContent: 'flex-end' }}>
                  <IconButton
                    color="inherit"
                    onClick={handleMenuOpen}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    {adminMenuItems.map((item) => (
                      <MenuItem
                        key={item.path}
                        component={Link}
                        to={item.path}
                        onClick={handleMenuClose}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                    <MenuItem
                      component={Link}
                      to="/"
                      onClick={() => {
                        handleMenuClose();
                        handleLogout();
                      }}
                    >
                      Logout
                    </MenuItem>
                  </Menu>
                </Box>

                {/* Desktop: Logout button */}
                <Button
                  color="inherit"
                  component={Link}
                  to="/"
                  onClick={handleLogout}
                  sx={{ display: { xs: 'none', md: 'inline-flex' }, whiteSpace: 'nowrap' }}
                >
                  Logout
                </Button>
              </>
            )}
          </Toolbar>
        </AppBar>
      )}

      <Container sx={{ flexGrow: 1, py: 3 }}>
        <Routes>
          <Route path="/" element={<EventList />} />
          <Route path="/register/:eventHashId" element={<RegistrationForm />} />
          <Route path="/lead/quick-enquery" element={<QuickEnqueryForm />} />
          <Route path="/verify-otp/:registrationHashId" element={<OtpVerification />} />
          <Route path="/payment/:registrationHashId" element={<Payment />} />
          <Route path="/phonepe/payment/:registrationHashId" element={<Payment />} />
          <Route path="/phonepe/status/:merchantTransactionId" element={<Status />} />
          <Route path="/event-pass/:registrationHashId" element={<EventPass />} />
          <Route path="/certificate/:certificateHashId" element={<Certificate />} />
          <Route path="/groupedevent/:groupId" element={<GroupedEvents />} />
          <Route path="/admin" element={<Login />} />
          <Route path="/admin/home" element={<UserHome />} />
          <Route path="/admin/events" element={<EventManagement />} />
          <Route path="/admin/coupons" element={<CouponManagement />} />
          <Route path="/admin/event-groups" element={<EventGroupManagement />} />
          <Route path="/admin/shorturls" element={<ShortUrlManagement />} />
          <Route path="/admin/reports" element={<Reports />} />
          <Route path="/admin/attendance" element={<CssVarsProvider><AttendanceManagement /></CssVarsProvider>} />
          <Route path="/admin/certificates" element={<CssVarsProvider><CertificateManagement /></CssVarsProvider>} />
          <Route path="/admin/certificate-analytics" element={<CssVarsProvider><CertificateAnalytics /></CssVarsProvider>} />
          <Route path="/admin/event-list" element={<EventList />} />
          <Route path="/phonepe/pay/:hallticketid" element={<PaymentLink />} />
          <Route path="/phonepe/payv1/:hallticketid" element={<PaymentLinkv1 />} />
          <Route path="/phonepe/dashboard" element={<PaymentLinkDashboard />} />
          <Route path="/pay/:hallticketid" element={<PaymentLink />} />
          <Route path="/dashboard" element={<PaymentLinkDashboard />} />
          <Route path="/thankyou/:registrationHashId" element={<ThankYou />} />
        </Routes>
      </Container>

      <Footer />
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
